import { useState, useCallback, useRef, useEffect, type FC } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'src/hooks/use-router';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import SearchMdIcon from '@untitled-ui/icons-react/build/esm/SearchMd';
import XCloseIcon from '@untitled-ui/icons-react/build/esm/XClose';
import Stars01Icon from '@untitled-ui/icons-react/build/esm/Stars01';
import type { Theme } from '@mui/material';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Popper,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import { AccountButton } from '../account-button';
import { NotificationsButton } from '../notifications-button';
import { SIDE_NAV_WIDTH } from './side-nav';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { leadsApi } from 'src/api/leads';
import type { LeadV5 } from 'src/api/types';
import { getInitials } from 'src/utils/get-initials';
import { AgentDialog } from 'src/components/ai-agent';
import { useCompanySettings } from 'src/hooks/use-company-settings';

export const TOP_NAV_HEIGHT: number = 64;

interface TopNavProps {
  onMobileNavOpen?: () => void;
  sideNavWidth?: number;
}

export const TopNav: FC<TopNavProps> = (props) => {
  const { onMobileNavOpen, sideNavWidth = SIDE_NAV_WIDTH, ...other } = props;
  const router = useRouter();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { settings: company } = useCompanySettings();

  // Customer search state
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<LeadV5[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Show results when focused and has value or results
  const showResults = isFocused && (searchValue.length > 0 || searchResults.length > 0);

  // Debounced search function - filters to only show names starting with query
  const searchCustomers = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Fetch more results since we'll filter client-side
      const response = await leadsApi.getAll({ search: query, pageSize: 50 });
      const allLeads = response.data.map((lead: any) => {
        if (lead.attributes) {
          return { ...lead.attributes, id: lead.id, documentId: lead.documentId };
        }
        return lead;
      }) as LeadV5[];

      // Filter to only show customers whose first or last name STARTS with the query
      const queryLower = query.toLowerCase().trim();
      const filteredLeads = allLeads.filter((lead) => {
        const firstName = (lead.firstName || '').toLowerCase();
        const lastName = (lead.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`;

        // Match if first name starts with query, last name starts with query,
        // or full name starts with query
        return firstName.startsWith(queryLower) ||
               lastName.startsWith(queryLower) ||
               fullName.startsWith(queryLower);
      });

      // Limit to 10 results
      setSearchResults(filteredLeads.slice(0, 10));
    } catch (error) {
      // console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Show loading immediately
    setIsSearching(true);

    // Debounce the actual API call
    searchTimeoutRef.current = setTimeout(() => {
      searchCustomers(value);
    }, 200);
  }, [searchCustomers]);

  // Handle customer selection
  const handleSelectCustomer = useCallback((customer: LeadV5) => {
    const customerId = customer.documentId || String(customer.id);
    router.push(`/dashboard/customers/${customerId}`);
    setSearchValue('');
    setSearchResults([]);
    setIsFocused(false);
    searchInputRef.current?.blur();
  }, [router]);

  // Handle close results
  const handleCloseResults = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setSearchResults([]);
    searchInputRef.current?.focus();
  }, []);

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // Re-search if there's a value
    if (searchValue.trim()) {
      searchCustomers(searchValue);
    }
  }, [searchValue, searchCustomers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Scroll state for background effect
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // AI Agent dialog state
  const [agentOpen, setAgentOpen] = useState(false);

  // OpenClaw popover state
  const [openClawAnchor, setOpenClawAnchor] = useState<HTMLElement | null>(null);
  const openClawPopoverOpen = Boolean(openClawAnchor);

  const handleOpenClawClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setOpenClawAnchor(event.currentTarget);
  }, []);

  const handleOpenClawClose = useCallback(() => {
    setOpenClawAnchor(null);
  }, []);

  return (
    <Box
      component="header"
      sx={{
        background: isScrolled
          ? isDarkMode
            ? 'rgba(10, 10, 11, 0.8)'
            : 'rgba(248, 250, 252, 0.8)'
          : 'transparent',
        backdropFilter: isScrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: isScrolled ? 'blur(12px)' : 'none',
        borderBottom: isScrolled
          ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`
          : '1px solid transparent',
        borderRadius: '0px',
        position: 'sticky',
        left: 0,
        top: 0,
        width: '100%',
        zIndex: 2,
        pl: {
          lg: `${sideNavWidth}px`
        },
        transition: 'padding-left 0.2s ease-in-out, background 0.2s ease-in-out, border-color 0.2s ease-in-out, backdrop-filter 0.2s ease-in-out',
      }}
      {...other}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{
          minHeight: TOP_NAV_HEIGHT,
          px: 2.5,
          pt: 1,
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing={2}
          sx={{ flex: 1 }}
        >
          {/* Logo - positioned in the nav area, centered with nav icons */}
          {lgUp && (
            <Box
              component={RouterLink}
              href={paths.index}
              sx={{
                position: 'absolute',
                left: 0,
                top: 6,
                height: 64,
                width: sideNavWidth,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 52,
                  width: 52,
                  borderRadius: isDarkMode ? '12px' : '50%',
                  overflow: 'hidden',
                  background: isDarkMode ? 'transparent' : '#000',
                  border: isDarkMode
                    ? '1px solid rgba(255,255,255,0.15)'
                    : '1px solid rgba(0,0,0,0.85)',
                  boxShadow: isDarkMode
                    ? '0 4px 16px rgba(0,0,0,0.4)'
                    : '0 2px 8px rgba(0,0,0,0.18)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: isDarkMode
                      ? '0 6px 20px rgba(0,0,0,0.5)'
                      : '0 4px 12px rgba(0,0,0,0.25)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/assets/logo-main.png"
                  alt="Mystic Media"
                  sx={{
                    height: isDarkMode ? 52 : 36,
                    width: 'auto',
                    objectFit: 'contain',
                    borderRadius: isDarkMode ? '12px' : '50%',
                  }}
                />
              </Box>
            </Box>
          )}
          {!lgUp && (
            <IconButton
              onClick={onMobileNavOpen}
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(250, 250, 250, 0.7)' : 'rgba(9, 9, 11, 0.7)',
                borderRadius: '6px',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <SvgIcon sx={{ fontSize: 20 }}>
                <Menu01Icon />
              </SvgIcon>
            </IconButton>
          )}

          {/* Customer Search - Instagram Style */}
          <Box
            ref={searchBoxRef}
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: mdUp ? 340 : 240,
              ml: lgUp ? 0 : 1,
            }}
          >
            <TextField
              inputRef={searchInputRef}
              fullWidth
              size="small"
              value={searchValue}
              onChange={handleSearchChange}
              onFocus={handleFocus}
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon
                      sx={{
                        color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
                        fontSize: 18,
                      }}
                    >
                      <SearchMdIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    {isSearching ? (
                      <CircularProgress size={16}
sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)' }} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{
                          p: 0.25,
                          bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                          '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)',
                          },
                        }}
                      >
                        <SvgIcon sx={{ fontSize: 14, color: isDarkMode ? '#fff' : '#1e293b' }}>
                          <XCloseIcon />
                        </SvgIcon>
                      </IconButton>
                    )}
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  height: 52,
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'}`,
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                  },
                  '&.Mui-focused': {
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                  },
                },
                '& .MuiInputAdornment-root': {
                  marginTop: '0 !important',
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem',
                  fontWeight: 400,
                  py: 1,
                  color: isDarkMode ? '#fff' : '#1e293b',
                  '&::placeholder': {
                    color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
                    opacity: 1,
                  },
                },
              }}
            />

            {/* Search Results Dropdown - Instagram Style */}
            <Popper
              open={showResults}
              anchorEl={searchBoxRef.current}
              placement="bottom"
              style={{
                zIndex: 1400,
                width: mdUp ? 375 : 320,
              }}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
            >
              <ClickAwayListener onClickAway={handleCloseResults}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '#262626'
                        : '#fff',
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 4px 24px rgba(0, 0, 0, 0.4)'
                        : '0 4px 24px rgba(0, 0, 0, 0.15)',
                    border: '1px solid',
                    borderColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.05)',
                  }}
                >
                  {/* Dropdown Arrow */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -6,
                      left: '50%',
                      transform: 'translateX(-50%) rotate(45deg)',
                      width: 12,
                      height: 12,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '#262626'
                          : '#fff',
                      borderTop: '1px solid',
                      borderLeft: '1px solid',
                      borderColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.05)',
                    }}
                  />

                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {isSearching && searchResults.length === 0 ? (
                      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24}
sx={{ color: 'text.secondary' }} />
                      </Box>
                    ) : searchValue && searchResults.length === 0 && !isSearching ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography variant="body2"
color="text.secondary">
                          No results found.
                        </Typography>
                      </Box>
                    ) : searchResults.length === 0 ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography variant="body2"
color="text.secondary">
                          Search for customers
                        </Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {searchResults.map((customer) => {
                          const name = `${customer.firstName} ${customer.lastName}`;
                          const subtitle = customer.email || customer.phone ||
                            [customer.city, customer.state].filter(Boolean).join(', ');

                          return (
                            <ListItemButton
                              key={customer.documentId || customer.id}
                              onClick={() => handleSelectCustomer(customer)}
                              sx={{
                                py: 1,
                                px: 2,
                                '&:hover': {
                                  bgcolor: (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255,255,255,0.05)'
                                      : 'rgba(0,0,0,0.03)',
                                },
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 52 }}>
                                <Avatar
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    fontSize: '0.95rem',
                                    fontWeight: 500,
                                    bgcolor: (theme) =>
                                      theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.1)'
                                        : 'rgba(0,0,0,0.08)',
                                    color: 'text.primary',
                                  }}
                                >
                                  {getInitials(name)}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    sx={{ lineHeight: 1.4 }}
                                  >
                                    {name}
                                  </Typography>
                                }
                                secondary={subtitle && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ lineHeight: 1.4 }}
                                  >
                                    {subtitle}
                                  </Typography>
                                )}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
        </Stack>
        <Stack
          alignItems="center"
          direction="row"
          spacing={1}
        >
          <NotificationsButton />
          <AccountButton />
        </Stack>

        {/* AI Agent Dialog */}
        <AgentDialog open={agentOpen} onClose={() => setAgentOpen(false)} />
      </Stack>
    </Box>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func,
  sideNavWidth: PropTypes.number
};
